import React from 'react';

import PropTypes from 'prop-types';

import { Grid } from '@iris/react-components';

import { ListColumns, SubHeading } from 'reaxl';

// Different styling variants for different contexts
const variants = {
    tab: {
        containerClass: 'padding-top-5',
        headingClass: 'padding-left-2 padding-bottom-4',
        listClass: 'padding-left-2',
        headingSize: '300',
        sm: 2,
        lg: 3,
    },
    modal: {
        containerClass: '',
        headingClass: 'padding-bottom-2',
        listClass: 'padding-left-2',
        headingSize: '200',
        gridColumns: { xs: 1, sm: 2, lg: 2, xl: 2 },
    },
};

/**
 * PackageItem component renders a package with its details in different contexts (tab or modal).
 *
 * @param {Object} props - The component props
 * @param {string|Object} props.item - The package item. Can be a string or an object with name and details properties
 * @param {string} props.item.name - The package name (when item is an object)
 * @param {string} props.item.details - Comma-separated package details (when item is an object)
 * @param {string} [props.context='tab'] - The rendering context ('tab' or 'modal')
 * @returns {JSX.Element} The rendered package item
 */
export default function PackageItem({ item, context }) {
    let columnizedValues = [];
    let name = item;

    if (typeof item !== 'string' && Object.keys(item).length) {
        const { details } = item;
        columnizedValues = details ? details.split(', ').reverse() : [];
        name = item.name;
    }

    const config = variants[context] || variants.tab;
    if (context === 'modal') {
        return (
            <div
                className={config.containerClass}
                key={item}
            >
                <SubHeading
                    className={config.headingClass}
                    size={config.headingSize}
                    level={4}
                >
                    {name.toUpperCase()}
                </SubHeading>
                <Grid
                    columns={config.gridColumns}
                    className="iris:pb-8 iris:gap-4"
                >
                    {columnizedValues.map((value, index) => (
                        <span key={`${value}-${index}`}>{value}</span>
                    ))}
                </Grid>
            </div>
        );
    }

    return (
        <div
            className={config.containerClass}
            key={item}
        >
            <SubHeading
                className={config.headingClass}
                componentClass="div"
                size={config.headingSize}
            >
                {name}
            </SubHeading>
            <ListColumns
                className={config.listClass}
                items={columnizedValues}
                sm={config.sm}
                lg={config.lg}
                expanded
            />
        </div>
    );
}

PackageItem.propTypes = {
    item: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            details: PropTypes.string,
        }),
    ]).isRequired,
    context: PropTypes.oneOf(['tab', 'modal']).isRequired,
};
